import { Component, input } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { GoalKeyInterface } from '@vendasta/ai-assistants';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { AiAssistantI18nModule } from '../../assets/i18n/ai-assistant-i18n.module';
import {
  AiSituationMappingForm,
  AiSituationConfigForm,
  areGoalKeysEqual,
} from '../../core/forms/ai-situation-config.form';
import { AiCapabilityForm } from '../../core/forms/ai-capability.form';

@Component({
  selector: 'ai-situation-configuration-form',
  imports: [
    AiAssistantI18nModule,
    MatCardModule,
    GalaxyFormFieldModule,
    ReactiveFormsModule,
    FormsModule,
    MatInputModule,
    MatChipsModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatIconModule,
  ],
  templateUrl: './ai-situation-configuration-form.component.html',
  styleUrl: './ai-situation-configuration-form.component.scss',
})
export class AiSituationConfigurationFormComponent {
  readonly situationConfigForm = input.required<AiSituationConfigForm>();
  readonly availableCapabilities = input.required<AiCapabilityForm[]>();

  addSituationMapping(): void {
    this.situationConfigForm().controls.situationMappings.push(new AiSituationMappingForm());
  }

  removeSituationMapping(index: number): void {
    this.situationConfigForm().controls.situationMappings.removeAt(index);
  }

  getAllCapabilityOptions(): Array<{ capabilityKey: GoalKeyInterface; name: string }> {
    return this.availableCapabilities()
      .filter((capability) => !capability.isHidden)
      .map((capability) => {
        const capabilityKey = capability.toGoalKey();
        if (!capabilityKey) return null;
        return {
          capabilityKey,
          name: capability.controls.name.value ?? '',
        };
      })
      .filter((option): option is { capabilityKey: GoalKeyInterface; name: string } => option !== null);
  }

  getSelectedCapabilities(
    mappingForm: AiSituationMappingForm,
  ): Array<{ capabilityKey: GoalKeyInterface; name: string }> {
    const selectedKeys = mappingForm.controls.goalKeys.value ?? [];
    return selectedKeys
      .map((key) => {
        const option = this.getAllCapabilityOptions().find((opt) => areGoalKeysEqual(opt.capabilityKey, key));
        return option || null;
      })
      .filter((opt): opt is { capabilityKey: GoalKeyInterface; name: string } => opt !== null);
  }

  getAvailableCapabilities(
    mappingForm: AiSituationMappingForm,
  ): Array<{ capabilityKey: GoalKeyInterface; name: string }> {
    const selectedKeys = mappingForm.controls.goalKeys.value ?? [];
    return this.getAllCapabilityOptions().filter((option) => {
      return !selectedKeys.some((selectedKey) => areGoalKeysEqual(option.capabilityKey, selectedKey));
    });
  }

  removeCapability(mappingForm: AiSituationMappingForm, capabilityKey: GoalKeyInterface): void {
    const currentKeys = mappingForm.controls.goalKeys.value ?? [];
    mappingForm.controls.goalKeys.setValue(currentKeys.filter((key) => !areGoalKeysEqual(key, capabilityKey)));
  }

  // Store search controls for each mapping form
  private readonly searchControls = new Map<AiSituationMappingForm, FormControl<string | null>>();

  getSearchControl(mappingForm: AiSituationMappingForm): FormControl<string | null> {
    if (!this.searchControls.has(mappingForm)) {
      this.searchControls.set(mappingForm, new FormControl<string | null>(''));
    }
    return this.searchControls.get(mappingForm)!;
  }

  getFilteredCapabilities(
    mappingForm: AiSituationMappingForm,
  ): Array<{ capabilityKey: GoalKeyInterface; name: string }> {
    const searchControl = this.getSearchControl(mappingForm);
    const searchTerm = searchControl.value?.toLowerCase() ?? '';
    const available = this.getAvailableCapabilities(mappingForm);
    if (!searchTerm) return available;
    return available.filter((option) => option.name.toLowerCase().includes(searchTerm));
  }

  addCapability(mappingForm: AiSituationMappingForm, event: MatAutocompleteSelectedEvent): void {
    const selectedOption = event.option.value as { capabilityKey: GoalKeyInterface; name: string };
    const currentKeys = mappingForm.controls.goalKeys.value ?? [];
    if (!currentKeys.some((key) => areGoalKeysEqual(key, selectedOption.capabilityKey))) {
      mappingForm.controls.goalKeys.setValue([...currentKeys, selectedOption.capabilityKey]);
    }
    // Clear the search input
    const searchControl = this.getSearchControl(mappingForm);
    searchControl.setValue('');
  }
}
